import { cn } from '@/lib/utils';
import { Progress } from '@/shared/ui/shadcn/progress';

const MIN_PROGRESS_PERCENT = 10;

export interface Step<T extends string = string> {
  id: T;
  label: string;
}

export interface StepProgressProps<T extends string = string> {
  steps: Array<Step<T>>;
  currentStepId: T;
  className?: string;
  showLabels?: boolean;
  easing?: 'linear' | 'easeOutQuad';
}

export function StepProgress<T extends string = string>({
  steps,
  currentStepId,
  className,
  showLabels = true,
  easing = 'linear',
}: StepProgressProps<T>) {
  const currentIndex = steps.findIndex((s) => s.id === currentStepId);
  const idx = currentIndex < 0 ? 0 : currentIndex;
  const t = steps.length > 1 ? idx / (steps.length - 1) : 1;

  const eased = (() => {
    switch (easing) {
      case 'easeOutQuad':
        return 1 - (1 - t) * (1 - t);
      case 'linear':
        return t;
      default:
        return t;
    }
  })();

  const value = Math.max(MIN_PROGRESS_PERCENT, eased * 100);

  return (
    <div className={cn('flex w-full flex-col gap-2', className)}>
      {showLabels ? (
        <div className="text-muted-foreground flex justify-between text-xs font-medium">
          {steps.map((step, i) => (
            <span
              key={step.id}
              className={cn(
                'transition-colors',
                i === idx ? 'text-foreground' : 'text-muted-foreground'
              )}
            >
              {step.label}
            </span>
          ))}
        </div>
      ) : null}
      <Progress value={value} className="h-1" />
    </div>
  );
}
